import React from 'react'
import { motion } from 'framer-motion'

interface LogoProps {
  variant?: 'text' | 'round' | 'combined'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  animated?: boolean
  className?: string
}

const Logo: React.FC<LogoProps> = ({
  variant = 'combined',
  size = 'md',
  animated = true,
  className = ''
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'h-8'
      case 'md': return 'h-12'
      case 'lg': return 'h-16'
      case 'xl': return 'h-24'
      default: return 'h-12'
    }
  }

  const LogoImage = ({ src, alt, className: imgClassName = '' }: { src: string; alt: string; className?: string }) => (
    <motion.img
      src={src}
      alt={alt}
      className={`${getSizeClasses()} ${imgClassName}`}
      initial={animated ? { opacity: 0, scale: 0.8 } : {}}
      animate={animated ? { opacity: 1, scale: 1 } : {}}
      transition={animated ? { duration: 0.6, type: "spring", stiffness: 300 } : {}}
      whileHover={animated ? { scale: 1.05 } : {}}
    />
  )

  const renderLogo = () => {
    switch (variant) {
      case 'text':
        return (
          <LogoImage
            src="/assets/images/logo-text.png"
            alt="Réminiscence - Logo avec texte"
            className="object-contain"
          />
        )

      case 'round':
        return (
          <LogoImage
            src="/assets/images/logo-round.png"
            alt="Réminiscence - Logo rond"
            className="object-contain"
          />
        )

      case 'combined':
        return (
          <div className="flex items-center space-x-4">
            <LogoImage
              src="/assets/images/logo-round.png"
              alt="Réminiscence - Logo rond"
              className="object-contain"
            />
            <div className="flex flex-col">
              <motion.h1
                className="text-2xl md:text-3xl font-serif tracking-wider text-[var(--text-primary)]"
                initial={animated ? { opacity: 0, x: -20 } : {}}
                animate={animated ? { opacity: 1, x: 0 } : {}}
                transition={animated ? { duration: 0.6, delay: 0.2 } : {}}
              >
                RÉMINISCENCE
              </motion.h1>
              <motion.p
                className="text-xs text-[var(--text-secondary)] font-sans tracking-[0.3em] uppercase"
                initial={animated ? { opacity: 0, x: -20 } : {}}
                animate={animated ? { opacity: 1, x: 0 } : {}}
                transition={animated ? { duration: 0.6, delay: 0.4 } : {}}
              >
                L'Écho des Souvenirs
              </motion.p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className={`flex items-center ${className}`}>
      {renderLogo()}
    </div>
  )
}

export default Logo
